import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { KPITileConfig, KPIOperation } from "../../types/kpi-types";
import { TestSuite } from "store/src/lib/tests/entity";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  field: z.string().min(1, "Field is required"),
  operation: z.enum(['count', 'distinct_count', 'sum', 'avg', 'min', 'max']),
});

type FormValues = z.infer<typeof formSchema>;

interface KPIConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (config: KPITileConfig) => void;
  testSuites: TestSuite[];
  initialConfig?: KPITileConfig;
}

export function KPIConfigDialog({
  open,
  onOpenChange,
  onSave,
  testSuites,
  initialConfig,
}: KPIConfigDialogProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: initialConfig?.title || '',
      field: initialConfig?.field || '',
      operation: initialConfig?.operation || 'count',
    },
  });

  // Reset form when dialog opens/closes or initialConfig changes
  useMemo(() => {
    if (open) {
      form.reset({
        title: initialConfig?.title || '',
        field: initialConfig?.field || '',
        operation: initialConfig?.operation || 'count',
      });
    }
  }, [open, initialConfig, form]);

  // Get available fields (standard + custom)
  const availableFields = useMemo(() => {
    const fields: Array<{ value: string; label: string; type: 'text' | 'number' }> = [
      { value: 'status', label: 'Status', type: 'text' },
      { value: 'result', label: 'Rating', type: 'text' },
      { value: 'score', label: 'Score', type: 'number' },
      { value: 'asset', label: 'Asset', type: 'text' },
      { value: 'building', label: 'Building', type: 'text' },
      { value: 'level', label: 'Level', type: 'text' },
      { value: 'area', label: 'Area', type: 'text' },
      { value: 'element', label: 'Element', type: 'text' },
      { value: 'element_id', label: 'Element ID', type: 'text' },
      { value: 'test_specification', label: 'Test Specification', type: 'text' },
      { value: 'test_suite', label: 'Test Suite', type: 'text' },
    ];

    // Add custom fields from test suites
    testSuites.forEach((suite) => {
      suite.custom_fields?.forEach((field) => {
        if (field.active) {
          const isNumeric = field.type === 'number' || field.type === 'scrollable_number';
          fields.push({
            value: field.field_id,
            label: `${field.label} (${suite.title})`,
            type: isNumeric ? 'number' : 'text',
          });
        }
      });
    });

    return fields;
  }, [testSuites]);

  // Get available operations based on selected field type
  const availableOperations = useMemo(() => {
    const selectedField = form.watch('field');
    const field = availableFields.find((f) => f.value === selectedField);
    const fieldType = field?.type || 'text';

    const allOperations: Array<{ value: KPIOperation; label: string }> = [
      { value: 'count', label: 'Count' },
      { value: 'distinct_count', label: 'Distinct Count' },
      { value: 'sum', label: 'Sum' },
      { value: 'avg', label: 'Average' },
      { value: 'min', label: 'Minimum' },
      { value: 'max', label: 'Maximum' },
    ];

    // For text fields, only allow count and distinct_count
    if (fieldType === 'text') {
      return allOperations.filter((op) => 
        op.value === 'count' || op.value === 'distinct_count'
      );
    }

    // For numeric fields, allow all operations
    return allOperations;
  }, [form.watch('field'), availableFields]);

  const onSubmit = useCallback((values: FormValues) => {
    const config: KPITileConfig = {
      id: initialConfig?.id || crypto.randomUUID(),
      title: values.title,
      field: values.field,
      operation: values.operation,
    };
    onSave(config);
    onOpenChange(false);
    form.reset();
  }, [initialConfig, onSave, onOpenChange, form]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    form.reset();
  }, [onOpenChange, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {initialConfig ? 'Edit KPI Tile' : 'Add KPI Tile'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Total Defects" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="field"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Field</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      // Reset operation if current one is not valid for new field type
                      const newField = availableFields.find((f) => f.value === value);
                      const currentOp = form.getValues('operation');
                      const newOps = newField?.type === 'text'
                        ? ['count', 'distinct_count']
                        : ['count', 'distinct_count', 'sum', 'avg', 'min', 'max'];
                      if (!newOps.includes(currentOp)) {
                        form.setValue('operation', 'count');
                      }
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a field" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableFields.map((fieldOption) => (
                        <SelectItem key={fieldOption.value} value={fieldOption.value}>
                          {fieldOption.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="operation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Operation</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an operation" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableOperations.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">
                {initialConfig ? 'Update' : 'Add'} Tile
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

